import { DrawPathTool } from "@treegram/tools-draw-path";
import { useCallback, useEffect, useRef } from "react";

import * as THREE from "three";
import * as OBF from "@thatopen/components-front";
import * as FRAGS from "@thatopen/fragments";

import { Line, readMeasurePath, type MeasureData } from "@treegram/viewer-core";
import { DrawLineTool } from "@treegram/tools-draw-line";
import { DrawPolygonTool } from "@treegram/tools-draw-polygon";
import { CameraTool } from "@treegram/tools-camera";
import { HighlighterTool } from "@treegram/tools-highlighter";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";

import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";
import useStoreyMeasuresContext from "@/pages/takeoff/layouts/measures/layouts/storey-measures/context/measures/use-storey-measures-context.ts";

import {
    ModelingMeasureRepresentation,
    ModelingType,
} from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";
import Measure from "@/pages/takeoff/context/project-measure/models/measure.ts";
import useModelingToolsContext from "@/services/context/viewer/modeling-tools/use-modeling-tools-context.ts";

export interface EditingProps {
    measure?: Measure;
    editingPoints: THREE.Vector3[];
    editingColor: THREE.Color;
    editingAlpha: number;
    editingMeasureRepresentation: ModelingMeasureRepresentation;
    editingDataInformations?: Record<string, unknown>;
}

const useStoreyMeasuresEditingTool = () => {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();
    const { viewer, getTool, getToolByKeyName } = useViewerContext();
    const { measures, handleGeometriesSelected } = useStoreyMeasuresContext();
    const { setIsInEditingMode, isInEditingMode } = useModelingToolsContext();

    const currentEditingTypeRef = useRef<ModelingType>(ModelingType.Point);
    const currentEditingPropsRef = useRef<EditingProps | undefined>(undefined);

    const startEditing = useCallback(
        async (editingType: ModelingType, isSnappingEnabled: boolean, editingProps: EditingProps) => {
            if (!ensureCanEdit()) {
                return;
            }

            currentEditingTypeRef.current = editingType;
            currentEditingPropsRef.current = editingProps;

            const lineToolEditing = getToolByKeyName<DrawLineTool>("drawLineToolEditing");
            const cameraTool = getTool(CameraTool);
            const highlighter = getTool(HighlighterTool);

            if (cameraTool != null) {
                cameraTool.setNavigationMode("Plan");
            }

            if (highlighter != null) {
                await highlighter.deactivate();
            }

            const snappings = isSnappingEnabled
                ? [FRAGS.SnappingClass.LINE, FRAGS.SnappingClass.POINT, FRAGS.SnappingClass.FACE]
                : [];

            switch (editingType) {
                case ModelingType.Point:
                case ModelingType.Polyline: {
                    const tool = getToolByKeyName<DrawPathTool>("drawPathToolEditing");
                    const id = editingProps.measure?.geometryRepresentations[0]?.geometryId;
                    if (!tool || !id) return;
                    const form = editingType === ModelingType.Point ? "Point" : "Polyline";
                    tool.configure(
                        form,
                        editingProps.editingColor.clone().convertSRGBToLinear(),
                        editingProps.editingAlpha,
                        editingProps.editingDataInformations ?? {},
                        snappings,
                    );
                    await tool.activate();
                    const points =
                        readMeasurePath(editingProps.editingDataInformations) ?? editingProps.editingPoints;
                    tool.beginEdit({
                        id,
                        points,
                        holesPoints: [],
                        boundingbox: new THREE.Box3().setFromPoints(points),
                        normal: new THREE.Vector3(0, 1, 0),
                        color: editingProps.editingColor.clone().convertSRGBToLinear(),
                        alpha: editingProps.editingAlpha,
                        type: form === "Point" ? "AREA" : "POLYLINE",
                        measureRepresentation: editingProps.editingMeasureRepresentation,
                        dataInformations: editingProps.editingDataInformations,
                    });
                    await viewer?.hideItems([id]);
                    break;
                }
                case ModelingType.Line: {
                    if (lineToolEditing == null) return;
                    await lineToolEditing.activate();

                    lineToolEditing.setSnappings(snappings);
                    lineToolEditing.clear();
                    if (editingProps.editingDataInformations)
                        lineToolEditing.setDataInformations(editingProps.editingDataInformations);
                    const { measure } = editingProps;
                    const geometryId = measure?.geometryRepresentations[0]?.geometryId;
                    const editableLine = new Line(
                        new OBF.Line(editingProps.editingPoints[0], editingProps.editingPoints[1]),
                        editingProps.editingColor,
                        editingProps.editingAlpha,
                        editingProps.editingMeasureRepresentation,
                        editingProps.editingDataInformations,
                    );
                    if (geometryId) {
                        editableLine.id = geometryId;
                        await viewer?.hideItems([geometryId]);
                    }

                    lineToolEditing.addMeasure(editableLine);
                    lineToolEditing.editMeasure(editableLine.id);
                    break;
                }

                case ModelingType.Surface: {
                    break;
                }
                default:
                    break;
            }

            setIsInEditingMode(true);
        },
        [ensureCanEdit, viewer, getTool, getToolByKeyName, setIsInEditingMode],
    );

    const stopEditing = useCallback(
        async (commit = false) => {
            const shouldCommit = commit && ensureCanEdit();
            const lineToolEditing = getToolByKeyName<DrawLineTool>("drawLineToolEditing");
            const polygonToolEditing = getToolByKeyName<DrawPolygonTool>("drawPolygonToolEditing");
            const cameraTool = getTool(CameraTool);
            const highlighter = getTool(HighlighterTool);

            const pathToolEditing = getToolByKeyName<DrawPathTool>("drawPathToolEditing");
            const isPath =
                currentEditingTypeRef.current === ModelingType.Point ||
                currentEditingTypeRef.current === ModelingType.Polyline;
            if (shouldCommit && isPath && pathToolEditing) {
                const updated = pathToolEditing.commitEdit();
                currentEditingPropsRef.current?.measure?.setDataInformations(updated.dataInformations);
                const saved: MeasureData = { ...updated, color: updated.color.clone().convertLinearToSRGB() };
                viewer?.prepareItemToUpdate(saved);
                // Keep the edited preview visible until the next fragment save/reload.
                getTool(DrawPathTool)?.addMeasure(updated);
            } else if (shouldCommit) {
                lineToolEditing?.commitEdit();
                const measure = currentEditingPropsRef.current?.measure;
                const lines = lineToolEditing?.getMeasures();
                if (measure && lines && lines.size > 0) {
                    const line = [...lines.values()][lines.size - 1];

                    measures
                        .find(
                            (m) =>
                                m.geometryRepresentations[0]?.geometryId ===
                                measure.geometryRepresentations[0]?.geometryId,
                        )
                        ?.setDataInformations(line.dataInformations);

                    viewer?.prepareItemToUpdate({
                        id: measure.geometryRepresentations[0].geometryId,
                        points: [line.start, line.end],
                        boundingbox: new THREE.Box3().setFromPoints([line.start, line.end]),
                        normal: new THREE.Vector3(),
                        color: line.color,
                        alpha: line.alpha,
                        type: "LINE",
                        measureRepresentation: line.measureRepresentation,
                        dataInformations: line.dataInformations,
                    });
                }
            } else {
                const geometryId =
                    currentEditingPropsRef.current?.measure?.geometryRepresentations[0]?.geometryId;
                if (geometryId) {
                    await viewer?.showItems([geometryId]);
                }
            }

            if (cameraTool != null) {
                cameraTool.setNavigationMode("Orbit");
            }

            if (highlighter != null) {
                await highlighter.activate();
                highlighter.onItemsSelected.add(handleGeometriesSelected);
            }

            await pathToolEditing?.deactivate();
            lineToolEditing?.clear();
            await lineToolEditing?.deactivate();
            polygonToolEditing?.clear();
            await polygonToolEditing?.deactivate();

            currentEditingPropsRef.current = undefined;
            setIsInEditingMode(false);
        },
        [ensureCanEdit, getTool, getToolByKeyName, handleGeometriesSelected, measures, setIsInEditingMode, viewer],
    );

    useEffect(() => {
        if (canEdit || !isInEditingMode) return;
        stopEditing(false).catch((error: unknown) => {
            console.error("Impossible de suspendre l’outil de modification.", error);
        });
    }, [canEdit, isInEditingMode, stopEditing]);

    return {
        startEditing,
        stopEditing,
    };
};

export default useStoreyMeasuresEditingTool;
